import { useReducer, useState } from 'react';
import Observable from 'zen-observable';
import { registerCompleteSubscription } from '../config/appsync';
import { OnRegisterCompletedSubscription } from '../config/appsync/API';
type SubscriptionData = { value: { data: OnRegisterCompletedSubscription }};

export type RegistrationSubscriptionState = {
  inProgress: boolean,
  magicLink: null | string,
  error: null | string
}

export enum ACTION_TYPE {
  INITIALIZED = 'INITIALIZED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

type ACTION =
  | { type: ACTION_TYPE.INITIALIZED }
  | { type: ACTION_TYPE.COMPLETED; payload: string }
  | { type: ACTION_TYPE.FAILED, payload?: string }
;

type RegistrationResponse = { registrationId: string, appSyncToken: string };

export type SubscriptionResult = {
  state: RegistrationSubscriptionState,
  subscribe(response: RegistrationResponse | void): void
}

const getErrMessageByKey = (key?: string): string => {
  switch (key) {
  case 'NOT_AN_ATTENDEE':
    return `Your email address is not currently on the list of registered attendees for this event.`;
  default:
    return `
      Something went wrong while we were trying to sign you in. Please check your email 
      for your registration confirmation email, which contains details of how to sign in.
    `;
  }
};

const _defaults: RegistrationSubscriptionState = { inProgress: false, error: null, magicLink: null };

export function reducer(state: RegistrationSubscriptionState, action: ACTION): RegistrationSubscriptionState {
  switch (action.type) {
  case ACTION_TYPE.INITIALIZED:
    return { ..._defaults, inProgress: true };
  case ACTION_TYPE.COMPLETED:
    return { ..._defaults, magicLink: action.payload };
  case ACTION_TYPE.FAILED:
    return { ..._defaults, error: getErrMessageByKey(action.payload) };
  default:
    return _defaults;
  }
}


let timeoutId: number | null = null;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const ONE_MINUTE_TIMEOUT = 60*1000;
const initialState = { inProgress: false, magicLink: null, error: null };

export const useRegistrationSubscription = (): SubscriptionResult => {
  const [ state, dispatch ] = useReducer(reducer, initialState);
  const [ cancelled, cancel ] = useState<boolean>(false);
  const cancelSubscription = () => cancel(true);

  const initSubscription = (client: {unsubscribe(): void, closed: boolean}) => {
    timeoutId = window.setTimeout(() => {
      if (!client.closed) client.unsubscribe();
      subscriptionFailed();
      cancelSubscription();
    }, ONE_MINUTE_TIMEOUT);

    dispatch({ type: ACTION_TYPE.INITIALIZED });
  };
  const subscriptionCompleted = (payload: string) => dispatch({ type: ACTION_TYPE.COMPLETED, payload });
  const subscriptionFailed = (payload?: string) => dispatch({ type: ACTION_TYPE.FAILED, payload });

  return {
    state,
    subscribe(response: RegistrationResponse): void {
      const params = [ response?.registrationId, response?.appSyncToken ] as [ string, string ];

      if (!params.every(Boolean)) return;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore*/
      const client = (registerCompleteSubscription(...params) as Observable<SubscriptionData>)
        .subscribe({
          next({ value }: SubscriptionData): void {
            const url = value?.data?.onRegisterCompleted?.magicLink ?? '';
            const error = value?.data?.onRegisterCompleted?.error;

            if (!cancelled && !error) {
              window.location.replace(url);
              subscriptionCompleted(url);
            }

            if (!cancelled && error) {
              subscriptionFailed(error);
              if (timeoutId) window.clearTimeout(timeoutId);
            }

            client.unsubscribe();
          },
          error(): void {
            subscriptionFailed();
            client.unsubscribe();
          },
          complete(): void {
            if (timeoutId) window.clearTimeout(timeoutId);
            if (!client.closed) client.unsubscribe();
          }
        })
      ;

      initSubscription(client);
    }
  };
};
